import { zxcvbn, zxcvbnOptions, type Score } from '@zxcvbn-ts/core'
import * as zxcvbnCommon from '@zxcvbn-ts/language-common'
import * as zxcvbnEn from '@zxcvbn-ts/language-en'
import * as zxcvbnJa from '@zxcvbn-ts/language-ja'

/**
 * パスワード強度スコア (T-C2、`zxcvbn-ts` ベース)。
 *
 * design/css/theme.css の `.strength.s{0..4}` クラスと 1:1 対応する 0〜4 のスコアを返す。
 * 辞書ベースの実用的な強度推定 (OWASP / NIST 整合) で、Bitwarden / 1Password と同等。
 *
 * 設計判断:
 *   - **マスター鍵漏洩防止**: `zxcvbn` はクライアント側 only で計算する。生のパスワードは
 *     関数を出ない (CLAUDE.md §2 ルール 1 と整合)。score / feedback のみを返す。
 *   - **辞書 lazy 初期化**: 初回呼出で辞書を `setOptions`。登録画面に到達しないユーザーに
 *     辞書を読み込ませない (bundle 分割の下準備、現状は同一 chunk)。
 *   - **言語**: dictionaries / translations は ja / en の両方を一度に読み込む。
 *     UI 言語切替で辞書を再ロードしない (state 単純化)。
 *   - **空文字 → score 0**: 簡易版の API と互換 (常に `PasswordStrengthScore` を返す)。
 *     UI 側は `.strength.s0` で全 segment 灰色 (theme.css は s1+ のみ着色) で表示。
 *   - **score の決定論性**: zxcvbn は決定論的 (同じ入力で常に同じ score)。spec で
 *     特定文字列の score を assertion してよい。
 *
 * 既知のリスク (受け皿あり):
 *   - 辞書 ~50 KB gzip 程度の bundle 増加 → release ノートで観測。Phase 4 で
 *     dynamic import への移行を検討。
 */

export type PasswordStrengthScore = Score

let initialized = false

const ensureInitialized = (): void => {
  if (initialized) return
  zxcvbnOptions.setOptions({
    translations: zxcvbnEn.translations,
    dictionary: {
      ...zxcvbnCommon.dictionary,
      ...zxcvbnEn.dictionary,
      ...zxcvbnJa.dictionary,
    },
    graphs: zxcvbnCommon.adjacencyGraphs,
  })
  initialized = true
}

/**
 * パスワード強度スコア (0-4) を返す。
 *
 * @param password マスターパスワード平文 (関数を出ない)
 * @param userInputs 個人情報をパスワードに含めると score を下げるヒント (email / 姓名)
 * @returns 0 (空文字 or 非常に弱い) 〜 4 (非常に強い)
 */
export const scorePasswordStrength = (
  password: string,
  userInputs?: readonly (string | number)[],
): PasswordStrengthScore => {
  if (password === '') return 0
  ensureInitialized()

  const result = zxcvbn(password, userInputs ? [...userInputs] : undefined)

  return result.score
}

/** テスト用: zxcvbn の lazy 初期化フラグをリセットする。 */
export const __resetPasswordStrengthForTest = (): void => {
  initialized = false
}
